import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex, Icon, LanguagePicker, Typography } from '@percent/lemonade'
import { useLocale } from '@percent/cause-dashboard/common/hooks'
import { LOCALE_LABELS, SupportedLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import styles from './LocalePicker.module.scss'
import classNames from 'classnames/bind'

const cx = classNames.bind(styles)

type LocalePickerProps = {
  variant?: 'inline' | 'sideMenu'
}

export function LocalePicker({ variant = 'inline' }: LocalePickerProps) {
  const { t } = useTranslation()
  const { locale, setLocale, supportedLocales } = useLocale()

  const [shaking, setShaking] = useState(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setShaking(true)
  }, [locale])

  return (
    <Flex className={cx(styles[variant], { [styles.shaking]: shaking })} onAnimationEnd={() => setShaking(false)}>
      <LanguagePicker
        options={supportedLocales.map(value => ({ value, label: LOCALE_LABELS[value] }))}
        value={locale}
        onChange={value => setLocale(value as SupportedLocale)}
        title={t('languagePicker.title')}
        triggerLabel={t('languagePicker.openLabel')}
        position={variant === 'sideMenu' ? 'top-start' : 'bottom-end'}
        selectOrientation={variant === 'sideMenu' ? 'up' : 'down'}
      >
        <Flex className={styles.disclaimer}>
          <Icon name="info" color="primary400" size="s" />
          <Flex className={styles.disclaimerText}>
            <Typography variant="default-bold" $color="primary400">
              {t('languagePicker.disclaimerTitle')}
            </Typography>
            <Typography variant="small" $color="primary400">
              {t('languagePicker.disclaimerBody')}
            </Typography>
          </Flex>
        </Flex>
      </LanguagePicker>
    </Flex>
  )
}
